import { apiService, API_ENDPOINTS, buildEndpoint } from "@/shared/services/api"
import type { APIResponse } from "@/shared/types"
import type { 
  OverrideRate, 
  OverrideRateStats, 
  OverrideRateFilters, 
  OverrideRateValidationResult
} from "../types"

export class OverrideRateService {
  /**
   * Get all override rates with filters and pagination
   */
  static async getOverrideRates(filters?: OverrideRateFilters): Promise<{
    overrideRates: OverrideRate[]
    total: number
    currentPage: number
    totalPages: number
  }> {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value))
      }
    })

    const response = await apiService.get<APIResponse<{
      overrideRates: OverrideRate[]
      total: number
      currentPage: number
      totalPages: number
    }>>(`${API_ENDPOINTS.hr.overrideRates}?${params.toString()}`)
    
    return response.data.data!
  }

  /**
   * Get override rate by ID
   */
  static async getOverrideRateById(id: string): Promise<OverrideRate> {
    const response = await apiService.get<APIResponse<OverrideRate>>(
      buildEndpoint(API_ENDPOINTS.hr.overrideRatesById, { id })
    )
    return response.data.data!
  }

  /**
   * Create a new override rate
   */
  static async createOverrideRate(data: {
    organizationId: string
    campaignId: string
    agentId: string
    overrideRate: number
    jobType: "SALES_AGENT" | "LEAD_GENERATION" | "EXECUTIVE_AGENT" | "MASTER_ORGANIZATION"
    enableOverrideTier: boolean
    volumeTier1Min: number
    volumeTier1Max: number
    tier1PayRate: number
    volumeTier2Min: number
    volumeTier2Max: number
    tier2PayRate: number
    volumeTier3Min: number
    volumeTier3Max: number
    tier3PayRate: number
    isActive: boolean
  }): Promise<{ success: boolean; message: string; data?: OverrideRate; warnings?: string[]; errors?: string[] }> {
    const response = await apiService.post<{ success: boolean; message: string; data?: OverrideRate; warnings?: string[]; errors?: string[] }>(
      API_ENDPOINTS.hr.overrideRates,
      data
    )
    return response.data
  }

  /**
   * Update override rate
   */
  static async updateOverrideRate(
    id: string, 
    data: Partial<OverrideRate>
  ): Promise<OverrideRateValidationResult & { data?: OverrideRate }> {
    try {
      const response = await apiService.put<any>(
        buildEndpoint(API_ENDPOINTS.hr.overrideRatesById, { id }),
        data
      )
      
      // Handle successful response
      if (response.data.success) {
        return {
          isValid: true,
          errors: [],
          warnings: response.data.warnings || [],
          data: response.data.data
        }
      }
      
      // Handle validation errors returned as success=false in response
      return {
        isValid: false,
        errors: response.data.errors || [],
        warnings: response.data.warnings || [],
        data: undefined
      }
    } catch (error: any) {
      // Handle HTTP error responses (400, 500, etc.)
      if (error.response?.data) {
        const responseData = error.response.data
        return {
          isValid: false,
          errors: responseData.errors || [responseData.message || 'Update failed'],
          warnings: responseData.warnings || [],
          data: undefined
        }
      }
      
      // Handle network or other errors
      throw error
    }
  }

  /**
   * Delete override rate
   */
  static async deleteOverrideRate(id: string): Promise<void> {
    await apiService.delete<APIResponse>(
      buildEndpoint(API_ENDPOINTS.hr.overrideRatesById, { id })
    )
  }

  /**
   * Get override rate statistics
   */
  static async getOverrideRateStats(filters?: OverrideRateFilters): Promise<OverrideRateStats> {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value))
      }
    })

    const response = await apiService.get<APIResponse<OverrideRateStats>>(
      `${API_ENDPOINTS.hr.overrideRatesStats}?${params.toString()}`
    )
    return response.data.data!
  }
}